import React, { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { Formik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import ReactDOMServer from "react-dom/server";
import {
  AppDatePicker,
  AppFileUploader,
  AppModal,
  AppRemixIcon,
  AppTextArea,
} from "@/layout/AppComponents";
import Translator from "../../../../../components/helpers/Translator";
import { axiosPath } from "../../../../../../routing/helpers/router";
import minions from "../../../../../../minions/minions";
import errorHandler from "../../../../../../layout/components/error/ErrorHandler";
import AppFileUploaderList from "../../../../../components/form/File/AppFileUploaderList";
import useIsMounted from "../../../../../components/hooks/useIsMounted";
import { handleDisabled, handleMinDate } from "../../tools/comsaReportsTools";

export default function ModuleComsaRecycledPackagingMaterialUpdate(props) {
  const { report, onRechargeReports, moduleData } = props;
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const [showModalReport, setShowModalReport] = useState(false);
  const validationSchema = Yup.object().shape({
    description: Yup.string()
      .nullable()
      .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
    year: Yup.date()
      .nullable()
      .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
  });

  const handleUpdateReport = useCallback(
    async (payload) =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaReports_editReport"),
          minions.forms.toFormData({
            ...payload,
            id: report.id,
            type: "recycledPackagingMaterial",
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
          }),
          { headers: { "Content-Type": "multipart/form-data" } }
        )
        .then(() => {
          if (isMounted.current) onRechargeReports();
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setShowModalReport(false)),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );

  return (
    <>
      <button
        type="button"
        className="btn btn-info btn-xs mr-1"
        onClick={() => setShowModalReport(true)}
        title={ReactDOMServer.renderToString(
          <Translator
            file="module-comsaReports"
            name="edit-recycled-packaging-material"
          />,
        )}
        disabled={handleDisabled(moduleData, report?.year)}
      >
        <AppRemixIcon icon="edit" />
      </button>

      <AppModal
        show={showModalReport}
        title={ReactDOMServer.renderToString(
          <Translator
            file="module-comsaReports"
            name="edit-recycled-packaging-material"
          />,
        )}
        size="lg"
        onClose={() => setShowModalReport(false)}
      >
        <Formik
          initialValues={{
            description: report.description,
            year: report.year,
            files: [],
            deletedFiles: [],
            comment: report.comment,
          }}
          validationSchema={validationSchema}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={handleUpdateReport}
        >
          {({ setFieldValue, handleSubmit, values, errors }) => (
            <>
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <AppDatePicker
                      title={<Translator file="module-comsaReports" name="year" />}
                      value={values.year}
                      onChange={(opt) => setFieldValue('year', opt)}
                      minDate={handleMinDate(moduleData)}
                      dateFormat="yyyy"
                      displayDateFormat="YYYY"
                      placeholder={
                        <Translator file="module-comsaReports" name="select.a.year" />
                      }
                      isClearable
                      showYearPicker
                      popperClassName="react-datepicker-popper react-datepicker-popper-1000"
                      error={errors.year}
                    />
                  </div>
                </div>
                <div className="col-12">
                  <div className="mb-3">
                    <AppTextArea
                      icon={<AppRemixIcon icon="font-color" noVariants />}
                      title={ReactDOMServer.renderToString(
                        <Translator file="module-comsaReports" name="description" />,
                      )}
                      value={values.description}
                      onChange={(v) => setFieldValue('description', v)}
                      isClearable
                      height="150px"
                      error={errors.description}
                    />
                  </div>
                </div>
                <div className="col-12">
                  <AppTextArea
                    title={ReactDOMServer.renderToString(
                      <Translator file="module-comsaReports" name="m-cr-comment" />,
                    )}
                    value={values?.comment}
                    onChange={(v) => setFieldValue(`comment`, v)}
                    height="89px"
                  />
                </div>
              </div>
              <div>
                <div className="text-primary mt-3">
                  <Translator file="module-comsaReports" name="attach.evidence" />
                </div>

                <div className="row mb-3">
                  <div className="col-sm-12 col-lg-6">
                    <AppFileUploader
                      isMulti
                      placeholder={
                        !values.files || Object.keys(values?.files).length === 0
                          ? ReactDOMServer.renderToString(
                              <Translator
                                file="module-legalCompliance"
                                name="select.one.multiple.files"
                              />,
                            )
                          : ReactDOMServer.renderToString(
                              <Translator
                                file="general"
                                name="select.number.of.elements"
                                variables={{
                                  elements: Object.keys(values?.files).length,
                                }}
                              />,
                            )
                      }
                      onChange={(opt) => setFieldValue('files', opt)}
                      btnText={<Translator file="general" name="search..." />}
                    />
                  </div>

                  <div className="col-sm-12 col-lg-6">
                    <AppFileUploaderList
                      values={values}
                      setFieldValue={setFieldValue}
                      urlsAvaliable={report.files}
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-12">
                  <button
                    className="btn btn-success btn-block"
                    type="submit"
                    onClick={handleSubmit}
                  >
                    <Translator file="module-comsaReports" name="edit" />
                  </button>
                </div>
              </div>
            </>
          )}
        </Formik>
      </AppModal>
    </>
  );
}
